// Carry Roll OptimizerPanel — tee/approach/fitting optimizers (10.7–10.9).

import { useState, useMemo } from 'react';
import { ExpressionInput } from 'calc-controls';
import { useRequest } from '../../store/selectors';
import { useStore } from '../../store';
import { teeOptimizer, approachOptimizer, fittingMode, apexConstrainedCarry } from '@carry-roll/shared-logic';
import { convertDistance } from '@carry-roll/shared-logic';
import { HelpTooltip } from '../calculator/HelpTooltip';

type Tab = 'tee' | 'approach' | 'fitting' | 'punch';

export function OptimizerPanel() {
  const request = useRequest();
  const [tab, setTab] = useState<Tab>('tee');

  return (
    <div data-testid="optimizer-panel">
      <h3 className="subsection-title">
        Optimizers
      </h3>
      <div className="flex gap-1 mb-3" role="tablist">
        {(['tee', 'approach', 'fitting', 'punch'] as const).map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            className="btn"
            style={{
              fontWeight: tab === t ? 'var(--fw-bold)' : 'var(--fw-regular)',
              opacity: tab === t ? 1 : 0.6,
            }}
            onClick={() => setTab(t)}
          >
            {t === 'tee' ? 'Tee shot' : t === 'approach' ? 'Approach' : t === 'fitting' ? 'Fitting' : 'Low punch'}
          </button>
        ))}
      </div>
      {tab === 'tee' && <TeeOptimizeView request={request} />}
      {tab === 'approach' && <ApproachOptimizeView request={request} />}
      {tab === 'fitting' && <FittingView request={request} />}
      {tab === 'punch' && <PunchView request={request} />}
    </div>
  );
}

function useDistConv() {
  const distanceUnit = useStore((s) => s.distanceUnit);
  return {
    distanceUnit,
    conv: (m: number) => Math.round(convertDistance(m, distanceUnit)),
  };
}

function TeeOptimizeView({ request }: { request: ReturnType<typeof useRequest> }) {
  const { distanceUnit, conv } = useDistConv();
  const fairwayWidth = useStore((s) => s.fairwayWidthM);
  const setFairwayWidth = useStore((s) => s.setFairwayWidth);
  const [offlineTol, setOfflineTol] = useState(15);

  const setups = useMemo(() => {
    return teeOptimizer(request, {
      fairwayWidthM: fairwayWidth,
      offlineToleranceM: offlineTol,
    });
  }, [request, fairwayWidth, offlineTol]);

  return (
    <div data-testid="tee-optimize-view">
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="block">
          <span className="field-label">
            Fairway width (m)
            <HelpTooltip label="Fairway width">Usable fairway width in metres.</HelpTooltip>
          </span>
          <ExpressionInput
            inline
            value={fairwayWidth}
            onChange={setFairwayWidth}
            unit="m"
            aria-label="Fairway width"
            decimals={0}
            min={0}
          />
        </div>
        <div className="block">
          <span className="field-label">
            Offline tolerance (m)
            <HelpTooltip label="Offline tolerance">Maximum lateral finish, in metres, still considered in play. Setups finishing wider are marked unplayable.</HelpTooltip>
          </span>
          <ExpressionInput
            inline
            value={offlineTol}
            onChange={setOfflineTol}
            unit="m"
            aria-label="Offline tolerance"
            decimals={0}
            min={0}
          />
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="w-full text-sm">
          <caption className="sr-only">Tee-shot optimizer — launch angle and spin by carry, total, offline</caption>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
              <th scope="col" style={{ textAlign: 'left', padding: '4px' }}>Launch</th>
              <th scope="col" style={{ textAlign: 'right', padding: '4px' }}>Carry</th>
              <th scope="col" style={{ textAlign: 'right', padding: '4px' }}>Total</th>
              <th scope="col" style={{ textAlign: 'right', padding: '4px' }}>Offline</th>
            </tr>
          </thead>
          <tbody>
            {setups.slice(0, 6).map((s, i) => (
              <tr
                key={i}
                data-testid={`tee-setup-${i}`}
                style={{ opacity: s.playable ? 1 : 0.5 }}
              >
                <td style={{ padding: '4px' }}>{s.launchAngleDeg}° / {s.spinRpm} rpm</td>
                <td style={{ textAlign: 'right', padding: '4px' }}>{conv(s.carryM)}</td>
                <td style={{ textAlign: 'right', padding: '4px', fontWeight: 'var(--fw-bold)' }}>
                  {conv(s.totalM)}
                </td>
                <td style={{ textAlign: 'right', padding: '4px' }}>{conv(s.offlineM)}{distanceUnit}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ApproachOptimizeView({ request }: { request: ReturnType<typeof useRequest> }) {
  const { conv } = useDistConv();
  const [targetCarry, setTargetCarry] = useState(150);

  const setups = useMemo(() => {
    return approachOptimizer(request, {
      targetCarryM: targetCarry,
      stopZoneM: 5,
    });
  }, [request, targetCarry]);

  return (
    <div data-testid="approach-optimize-view">
      <div className="block mb-3">
        <span className="field-label">
          Target carry (m)
          <HelpTooltip label="Target carry">The carry you want the approach to finish at. The optimizer scores launches by closeness plus stopping power.</HelpTooltip>
        </span>
        <ExpressionInput
          inline
          value={targetCarry}
          onChange={setTargetCarry}
          unit="m"
          aria-label="Target carry"
          decimals={0}
          min={0}
        />
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="w-full text-sm">
          <caption className="sr-only">Approach optimizer — launch angle by carry, descent, and stopping power</caption>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
              <th scope="col" style={{ textAlign: 'left', padding: '4px' }}>Launch</th>
              <th scope="col" style={{ textAlign: 'right', padding: '4px' }}>Carry</th>
              <th scope="col" style={{ textAlign: 'right', padding: '4px' }}>Descent</th>
              <th scope="col" style={{ textAlign: 'right', padding: '4px' }}>Stop</th>
            </tr>
          </thead>
          <tbody>
            {setups.slice(0, 6).map((s, i) => (
              <tr key={i} data-testid={`approach-setup-${i}`}>
                <td style={{ padding: '4px' }}>{s.launchAngleDeg}°</td>
                <td style={{ textAlign: 'right', padding: '4px' }}>{conv(s.carryM)}</td>
                <td style={{ textAlign: 'right', padding: '4px' }}>{s.landingAngleDeg}°</td>
                <td style={{ textAlign: 'right', padding: '4px' }}>{s.stoppingPower}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FittingView({ request }: { request: ReturnType<typeof useRequest> }) {
  const gains = useMemo(() => fittingMode(request), [request]);
  return (
    <div data-testid="fitting-view">
      <p className="text-sm mb-3" style={{ color: 'var(--color-text-muted)' }}>
        Ranked gain opportunities from current baseline.
      </p>
      <div className="flex flex-col gap-2">
        {gains.map((g, i) => (
          <div
            key={g.parameter}
            data-testid={`fitting-gain-${i}`}
            className="panel"
            style={{ padding: 'var(--space-2)' }}
          >
            <div className="flex justify-between">
              <span style={{ fontWeight: 'var(--fw-medium)' }}>
                {g.parameter}
              </span>
              <span
                style={{
                  color: g.expectedGainM > 0 ? 'var(--color-success)' : 'var(--color-danger)',
                  fontWeight: 'var(--fw-bold)',
                }}
              >
                {g.expectedGainM > 0 ? '+' : ''}
                {g.expectedGainM} m
              </span>
            </div>
            <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
              {g.current} → {g.optimized} ({g.delta})
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PunchView({ request }: { request: ReturnType<typeof useRequest> }) {
  const { distanceUnit, conv } = useDistConv();
  const [ceiling, setCeiling] = useState(20);

  const res = useMemo(() => apexConstrainedCarry(request, ceiling), [request, ceiling]);

  return (
    <div data-testid="apex-constrained-view">
      <p className="text-sm mb-3" style={{ color: 'var(--color-text-muted)' }}>
        Find the longest carry that keeps the apex under a ceiling — useful under tree limbs or wind.
      </p>
      <div className="block mb-3">
        <span className="field-label">
          Ceiling height (m)
          <HelpTooltip label="Ceiling height">Maximum apex in metres. Returns the longest carry whose peak stays below it.</HelpTooltip>
        </span>
        <ExpressionInput
          inline
          value={ceiling}
          onChange={setCeiling}
          unit="m"
          aria-label="Ceiling height"
          decimals={0}
          min={0}
        />
      </div>
      <div className="panel" style={{ padding: 'var(--space-3)' }}>
        {res.feasible ? (
          <>
            <div className="flex justify-between">
              <span className="text-sm">
                Recommended launch
              </span>
              <span style={{ fontWeight: 'var(--fw-bold)' }}>
                {res.launchAngleDeg}°
              </span>
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                Carry
              </span>
              <span style={{ fontWeight: 'var(--fw-medium)', color: 'var(--color-carry)' }}>
                {conv(res.carryM)} {distanceUnit}
              </span>
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                Apex
              </span>
              <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                {Math.round(res.apexM)} m
              </span>
            </div>
          </>
        ) : (
          <div
            className="badge inline-flex items-center"
            style={{ background: 'var(--color-danger)', color: 'white' }}
            role="status"
          >
            Not feasible — no launch keeps the apex under {ceiling} m. Lowest apex found: {Math.round(res.apexM)} m at {res.launchAngleDeg}°.
          </div>
        )}
      </div>
    </div>
  );
}
